import { useQuery } from 'react-query';
import { httpRequest } from '../config/api';

export const outputDataService = {
  getOutputData: params =>
    httpRequest.get('api/get-data', {
      params
    })
};

/* Long enough that alt-tabbing back and forth cannot turn the refetch below into a drumbeat on a
   heavy payload, short enough that a reader returning to the page after doing something else gets
   the current answer. Invalidation is unaffected: `invalidateQueries` marks a query stale whatever
   this says, so the SignalR handlers and the post-save invalidates still refetch at once. */
const OUTPUT_DATA_STALE_MS = 30000;

// The backend's own default, filled in so a request that omits it shares a cache entry with one that says it.
const outputDataKey = params => [
  'GET_OUTPUT_DATA',
  { ...params, add_custom_fields: !!params?.add_custom_fields }
];

// One period's output, or every period's when no id is given.
export const invalidateOutputData = (queryClient, periodId) =>
  queryClient.invalidateQueries({
    predicate: query =>
      query.queryKey[0] === 'GET_OUTPUT_DATA' &&
      (!periodId || query.queryKey[1]?.period_id === periodId)
  });

export const useGetOutputData = ({ queryOptions, params } = {}) => {
  // Key namespace was originally 'GET_PERIOD_BY_ID' — same root key used by
  // usePeriodById in period.service.js. React Query treats matching prefixes
  // as the same family for invalidation, so every period mutation invalidated
  // this query and forced a refetch of the heavy /api/get-data payload
  // (and vice versa). Move to a dedicated namespace so the caches no longer
  // collide.
  return useQuery(outputDataKey(params), () => outputDataService.getOutputData(params), {
    /* The output grid, its Errors panel and the column verdicts all read this, and a SignalR push
         was their only refresh — no interval anywhere, and `refetchOnWindowFocus` is false repo-wide
         (src/config/queryClient.js), so a lost push left them describing the previous rebuild until
         the page was reloaded.
         A heartbeat is the wrong answer here: /api/get-data is the heavy payload this file already
         warns about, and polling it is worse than the illness. Refetching when the reader comes back
         to the window costs nothing while they are away, and "back on the page" is the moment they
         are about to read it. This is a deliberate local exception to the global default — paired
         with a stale time so a burst of window switching cannot make it expensive.
         It does not cover a push lost while the reader sits and watches. Closing that needs a status
         endpoint light enough to poll, which does not exist yet. */
    staleTime: OUTPUT_DATA_STALE_MS,
    refetchOnWindowFocus: true,
    ...queryOptions
  });
};
